import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Trash2, Minus, Receipt, CreditCard, Banknote,
  ArrowRightLeft, Package, Stethoscope, CheckCircle2,
  Lock, Printer, Mail, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

/* ── Types ─────────────────────────────────────────────────────────────────── */

type ConsultaItem = {
  id: string;
  citaId: string;
  tipo: 'servicio' | 'producto';
  referenciaId: string | null;
  nombre: string;
  precioUnitario: number;
  cantidad: number;
  createdAt: string;
};

type Pago = {
  id: string;
  monto: number;
  totalRecibido: number | null;
  cambio: number | null;
  metodo: 'efectivo' | 'tarjeta' | 'transferencia' | 'stripe';
  estado: 'pendiente' | 'pagado' | 'reembolsado' | 'cancelado';
  referencia: string | null;
  pagadoEn: string | null;
  createdAt: string;
};

type Catalogo = { id: string; nombre: string; precio: number; stock?: number; stockMinimo?: number };
type SesionCaja = { id: string; estado: 'abierta' | 'cerrada'; fondoInicial: number; createdAt: string };

const METODO_LABELS: Record<string, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  stripe: 'Stripe',
};

const METODO_ICONS: Record<string, React.ElementType> = {
  efectivo: Banknote,
  tarjeta: CreditCard,
  transferencia: ArrowRightLeft,
  stripe: CreditCard,
};

const ESTADO_PAGO: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'Pendiente',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pagado:    { label: 'Pagado',       cls: 'bg-green-50 text-green-700 border-green-200' },
  reembolsado: { label: 'Reembolsado', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  cancelado: { label: 'Cancelado',    cls: 'bg-red-50 text-red-600 border-red-200' },
};

function fmt(n: number) {
  return Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

/* ── Catalog Picker ─────────────────────────────────────────────────────────── */

function CatalogPicker({
  tipo,
  items,
  onSelect,
  onClose,
}: {
  tipo: 'servicio' | 'producto';
  items: Catalogo[];
  onSelect: (item: Catalogo) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const filtered = items.filter(i => i.nombre.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100">
          <p className="font-semibold text-sm text-text-primary">
            {tipo === 'servicio' ? 'Agregar servicio' : 'Agregar producto'}
          </p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors">
            Cerrar
          </button>
        </div>
        <div className="p-3 border-b border-gray-100">
          <input
            autoFocus
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <div className="overflow-y-auto max-h-64">
          {filtered.length === 0 && (
            <p className="text-center py-8 text-sm text-text-secondary">
              {tipo === 'producto' ? 'Sin productos con stock disponible' : 'Sin resultados'}
            </p>
          )}
          {filtered.map(item => (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-primary-50 transition-colors text-left border-b border-gray-50 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm text-text-primary font-medium block">{item.nombre}</span>
                {tipo === 'producto' && item.stock !== undefined && (
                  <span className={cn('text-[10px] font-medium', item.stock <= (item.stockMinimo ?? 0) ? 'text-amber-600' : 'text-gray-400')}>
                    Stock: {item.stock}
                    {item.stock <= (item.stockMinimo ?? 0) && ' ⚠ bajo mínimo'}
                  </span>
                )}
              </div>
              <span className="text-sm text-primary-600 font-semibold ml-3 flex-shrink-0">{fmt(item.precio)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Pay Modal ──────────────────────────────────────────────────────────────── */

function PagoModal({
  total,
  pacienteId,
  citaId,
  clinicaId,
  onSuccess,
  onClose,
}: {
  total: number;
  pacienteId: string;
  citaId: string;
  clinicaId: string;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const [metodo, setMetodo] = useState<'efectivo' | 'tarjeta' | 'transferencia'>('efectivo');
  const [monto, setMonto] = useState(String(total.toFixed(2)));
  const [recibido, setRecibido] = useState('');
  const [referencia, setReferencia] = useState('');

  const montoNum = parseFloat(monto) || 0;
  const recibidoNum = parseFloat(recibido) || 0;
  const vuelta = metodo === 'efectivo' && recibidoNum > montoNum ? recibidoNum - montoNum : 0;

  const createPago = useMutation({
    mutationFn: (dto: any) => api.post('/api/pagos', dto).then(r => r.data),
    onSuccess: () => {
      toast.success('Pago registrado');
      onSuccess();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar pago')),
  });

  function handleSubmit() {
    if (isNaN(montoNum) || montoNum <= 0) { toast.error('Monto inválido'); return; }
    if (metodo === 'efectivo' && recibidoNum > 0 && recibidoNum < montoNum) {
      toast.error('El monto recibido es menor al cobro'); return;
    }
    createPago.mutate({
      citaId, pacienteId, clinicaId,
      monto: montoNum,
      metodo,
      referencia: referencia || undefined,
      totalRecibido: metodo === 'efectivo' && recibidoNum > 0 ? recibidoNum : undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-sm text-text-primary">Registrar pago</p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs px-2 py-1 rounded-lg hover:bg-gray-100">
            Cerrar
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Monto a cobrar (MXN)</label>
            <input
              type="number" min="0.01" step="0.01" value={monto}
              onChange={e => setMonto(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Método de pago</label>
            <div className="grid grid-cols-3 gap-2">
              {(['efectivo', 'tarjeta', 'transferencia'] as const).map(m => {
                const Icon = METODO_ICONS[m];
                return (
                  <button key={m} type="button" onClick={() => setMetodo(m)}
                    className={cn(
                      'flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-xl border text-xs font-medium transition-colors',
                      metodo === m ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-gray-200 text-text-secondary hover:bg-gray-50',
                    )}>
                    <Icon size={14} />
                    {METODO_LABELS[m]}
                  </button>
                );
              })}
            </div>
          </div>

          {metodo === 'efectivo' && (
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Recibido (opcional)</label>
              <input
                type="number" min="0" step="0.01" value={recibido}
                onChange={e => setRecibido(e.target.value)}
                placeholder={`Mínimo ${fmt(montoNum)}`}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono"
              />
              {vuelta > 0 && (
                <div className="mt-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-green-700">Cambio (vuelta)</span>
                  <span className="text-sm font-bold text-green-700">{fmt(vuelta)}</span>
                </div>
              )}
            </div>
          )}

          {(metodo === 'tarjeta' || metodo === 'transferencia') && (
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Referencia / Folio (opcional)</label>
              <input
                type="text" value={referencia} onChange={e => setReferencia(e.target.value)}
                placeholder="Ej. TRAN-0001, últimos 4 dígitos..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={createPago.isPending}
            className="flex items-center gap-2 text-sm bg-primary-600 hover:bg-primary-700 text-white px-5 py-2 rounded-lg disabled:opacity-60 transition-colors font-medium">
            <CheckCircle2 size={14} />
            {createPago.isPending ? 'Registrando...' : `Registrar ${fmt(montoNum)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Ticket Print ───────────────────────────────────────────────────────────── */

function imprimirTicket(items: ConsultaItem[], pagos: Pago[], subtotal: number, totalPagado: number) {
  const pagado = pagos.filter(p => p.estado === 'pagado');
  const fecha = new Date().toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  const html = `
    <html><head><title>Ticket</title><style>
      body{font-family:monospace;font-size:12px;width:300px;margin:0 auto;padding:16px}
      h2{text-align:center;font-size:14px;margin:0 0 4px}
      p{margin:2px 0}.line{border-top:1px dashed #000;margin:8px 0}
      .row{display:flex;justify-content:space-between}
      .bold{font-weight:bold}
    </style></head><body>
    <h2>TICKET DE VENTA</h2>
    <p style="text-align:center">${fecha}</p>
    <div class="line"></div>
    ${items.map(i => `
      <div class="row"><span>${i.nombre}</span></div>
      <div class="row"><span>${i.cantidad} x ${fmt(Number(i.precioUnitario))}</span><span>${fmt(Number(i.precioUnitario)*i.cantidad)}</span></div>
    `).join('')}
    <div class="line"></div>
    <div class="row bold"><span>TOTAL</span><span>${fmt(subtotal)}</span></div>
    ${pagado.map(p => `
      <div class="row"><span>${METODO_LABELS[p.metodo] || p.metodo}</span><span>${fmt(Number(p.monto))}</span></div>
      ${p.cambio ? `<div class="row"><span>Cambio</span><span>${fmt(Number(p.cambio))}</span></div>` : ''}
    `).join('')}
    <div class="line"></div>
    <p style="text-align:center">Gracias por su preferencia</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=400,height=600');
  if (w) { w.document.write(DOMPurify.sanitize(html)); w.document.close(); w.print(); }
}

/* ── CobroTab ───────────────────────────────────────────────────────────────── */

export function CobroTab({
  citaId,
  pacienteId,
  clinicaId,
  canEdit,
}: {
  citaId: string;
  pacienteId: string;
  clinicaId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [picker, setPicker] = useState<'servicio' | 'producto' | null>(null);
  const [showPagoModal, setShowPagoModal] = useState(false);

  /* Queries */
  const { data: items = [] } = useQuery<ConsultaItem[]>({
    queryKey: ['consulta-items', citaId],
    queryFn: () => api.get('/api/consulta-items', { params: { citaId } }).then(r => r.data),
    enabled: !!citaId,
  });

  const { data: pagos = [] } = useQuery<Pago[]>({
    queryKey: ['pagos-cita', citaId],
    queryFn: () => api.get('/api/pagos', { params: { citaId } }).then(r => r.data),
    enabled: !!citaId,
  });

  const { data: sesionCaja } = useQuery<SesionCaja | null>({
    queryKey: ['sesion-caja-activa'],
    queryFn: () => api.get('/api/caja/sesion-activa').then(r => r.data),
    staleTime: 30_000,
  });

  const { data: servicios = [] } = useQuery<Catalogo[]>({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data),
    enabled: picker === 'servicio',
  });

  const { data: productos = [] } = useQuery<Catalogo[]>({
    queryKey: ['productos-con-stock'],
    queryFn: () => api.get('/api/productos', { params: { soloConStock: 'true' } }).then(r => r.data),
    enabled: picker === 'producto',
  });

  /* Mutations */
  const addItem = useMutation({
    mutationFn: (dto: any) => api.post('/api/consulta-items', dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulta-items', citaId] });
      setPicker(null);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al agregar')),
  });

  const updateCantidad = useMutation({
    mutationFn: ({ id, cantidad }: { id: string; cantidad: number }) =>
      api.patch(`/api/consulta-items/${id}`, { cantidad }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulta-items', citaId] }),
  });

  const removeItem = useMutation({
    mutationFn: (id: string) => api.delete(`/api/consulta-items/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulta-items', citaId] }),
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar')),
  });

  const enviarEmail = useMutation({
    mutationFn: () => api.post(`/api/pacientes/${pacienteId}/enviar-ticket`, { citaId }).then(r => r.data),
    onSuccess: () => toast.success('Ticket enviado por email'),
    onError: (err: any) => toast.error(getErrorMessage(err, 'No se pudo enviar el email')),
  });

  /* Calculations */
  const subtotal = items.reduce((sum, i) => sum + Number(i.precioUnitario) * i.cantidad, 0);
  const totalPagado = pagos.filter(p => p.estado === 'pagado').reduce((sum, p) => sum + Number(p.monto), 0);
  const saldoPendiente = Math.max(0, subtotal - totalPagado);
  const estaCerrada = subtotal > 0 && saldoPendiente <= 0.01;
  const cajaAbierta = !!sesionCaja;

  function handleSelectCatalog(tipo: 'servicio' | 'producto', cat: Catalogo) {
    addItem.mutate({ citaId, pacienteId, clinicaId, tipo, referenciaId: cat.id, nombre: cat.nombre, precioUnitario: cat.precio, cantidad: 1 });
  }

  return (
    <div className="space-y-6">

      {/* ── Caja cerrada warning ── */}
      {!cajaAbierta && canEdit && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <Lock size={16} className="text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Caja cerrada</p>
            <p className="text-xs text-amber-700">No puedes registrar pagos sin una sesión de caja abierta. Ve a <strong>Finanzas &gt; Caja</strong>.</p>
          </div>
        </div>
      )}

      {/* ── Cita cerrada badge ── */}
      {estaCerrada && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-green-800">Cita cerrada — cobro completo</p>
          {pagos.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => imprimirTicket(items, pagos, subtotal, totalPagado)}
                className="flex items-center gap-1.5 text-xs font-medium text-green-700 hover:bg-green-100 px-2.5 py-1.5 rounded-lg transition-colors">
                <Printer size={12} /> Imprimir
              </button>
              <button
                onClick={() => enviarEmail.mutate()}
                disabled={enviarEmail.isPending}
                className="flex items-center gap-1.5 text-xs font-medium text-green-700 hover:bg-green-100 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                <Mail size={12} /> Email
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
          <p className="text-lg font-bold text-text-primary">{fmt(subtotal)}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-text-secondary mt-0.5">Total a cobrar</p>
        </div>
        <div className="rounded-xl border border-green-100 bg-green-50 p-3 text-center">
          <p className="text-lg font-bold text-green-700">{fmt(totalPagado)}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-green-600 mt-0.5">Pagado</p>
        </div>
        <div className={cn('rounded-xl border p-3 text-center', saldoPendiente > 0 ? 'border-amber-100 bg-amber-50' : 'border-gray-100 bg-gray-50')}>
          <p className={cn('text-lg font-bold', saldoPendiente > 0 ? 'text-amber-700' : 'text-text-secondary')}>
            {fmt(saldoPendiente)}
          </p>
          <p className={cn('text-[10px] font-medium uppercase tracking-wider mt-0.5', saldoPendiente > 0 ? 'text-amber-600' : 'text-text-secondary')}>
            Pendiente
          </p>
        </div>
      </div>

      {/* ── Items section ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Servicios / Productos</h3>
          {canEdit && !estaCerrada && (
            <div className="flex items-center gap-2">
              <button onClick={() => setPicker('servicio')}
                className="flex items-center gap-1.5 text-xs font-medium border border-dashed border-primary-300 text-primary-600 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={11} /><Stethoscope size={11} /> Servicio
              </button>
              <button onClick={() => setPicker('producto')}
                className="flex items-center gap-1.5 text-xs font-medium border border-dashed border-gray-300 text-text-secondary hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-colors">
                <Plus size={11} /><Package size={11} /> Producto
              </button>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 rounded-xl py-8 text-center">
            <Receipt size={20} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin servicios o productos asignados</p>
            {canEdit && <p className="text-xs text-text-secondary/60 mt-1">Usa los botones de arriba para agregar</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => (
              <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex-shrink-0">
                  {item.tipo === 'servicio'
                    ? <Stethoscope size={14} className="text-primary-500" />
                    : <Package size={14} className="text-gray-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{item.nombre}</p>
                  <p className="text-xs text-text-secondary">{fmt(item.precioUnitario)} c/u</p>
                </div>

                {canEdit && !estaCerrada ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        if (item.cantidad <= 1) removeItem.mutate(item.id);
                        else updateCantidad.mutate({ id: item.id, cantidad: item.cantidad - 1 });
                      }}
                      className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 text-gray-500 transition-colors">
                      <Minus size={10} />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold text-text-primary">{item.cantidad}</span>
                    <button
                      onClick={() => updateCantidad.mutate({ id: item.id, cantidad: item.cantidad + 1 })}
                      className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 text-gray-500 transition-colors">
                      <Plus size={10} />
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-text-secondary">×{item.cantidad}</span>
                )}

                <span className="text-sm font-semibold text-text-primary w-20 text-right flex-shrink-0">
                  {fmt(Number(item.precioUnitario) * item.cantidad)}
                </span>

                {canEdit && !estaCerrada && (
                  <button onClick={() => removeItem.mutate(item.id)}
                    className="p-1 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}

            {items.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 mt-1">
                <span className="text-sm font-semibold text-text-primary">Total</span>
                <span className="text-base font-bold text-primary-600">{fmt(subtotal)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Registrar pago button ── */}
      {canEdit && saldoPendiente > 0 && items.length > 0 && (
        <button
          onClick={() => cajaAbierta ? setShowPagoModal(true) : toast.error('Abre la caja antes de registrar pagos')}
          className={cn(
            'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors',
            cajaAbierta
              ? 'bg-primary-600 hover:bg-primary-700 text-white'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed',
          )}>
          {cajaAbierta ? <CreditCard size={14} /> : <Lock size={14} />}
          {cajaAbierta ? `Registrar pago · ${fmt(saldoPendiente)}` : 'Caja cerrada'}
        </button>
      )}

      {/* ── Print/email when not closed but has payments ── */}
      {!estaCerrada && pagos.filter(p => p.estado === 'pagado').length > 0 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => imprimirTicket(items, pagos, subtotal, totalPagado)}
            className="flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">
            <Printer size={12} /> Imprimir ticket parcial
          </button>
        </div>
      )}

      {/* ── Pagos registrados ── */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Historial de pagos</h3>
        {pagos.length === 0 ? (
          <p className="text-sm text-text-secondary italic">Sin pagos registrados</p>
        ) : (
          <div className="space-y-2">
            {pagos.map(pago => {
              const Icon = METODO_ICONS[pago.metodo] ?? CreditCard;
              const ep = ESTADO_PAGO[pago.estado] ?? { label: pago.estado, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
              const fecha = pago.pagadoEn
                ? new Date(pago.pagadoEn).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                : new Date(pago.createdAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
              return (
                <div key={pago.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <Icon size={14} className="text-text-secondary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{METODO_LABELS[pago.metodo]}</p>
                    <p className="text-xs text-text-secondary">
                      {fecha}
                      {pago.referencia ? ` · ${pago.referencia}` : ''}
                      {pago.cambio ? ` · Cambio: ${fmt(Number(pago.cambio))}` : ''}
                    </p>
                  </div>
                  <span className={cn('text-[10px] px-2 py-0.5 rounded-full border font-semibold flex-shrink-0', ep.cls)}>
                    {ep.label}
                  </span>
                  <span className="text-sm font-bold text-text-primary w-20 text-right flex-shrink-0">
                    {fmt(Number(pago.monto))}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {picker && (
        <CatalogPicker
          tipo={picker}
          items={picker === 'servicio' ? servicios : productos}
          onSelect={cat => handleSelectCatalog(picker, cat)}
          onClose={() => setPicker(null)}
        />
      )}

      {showPagoModal && (
        <PagoModal
          total={saldoPendiente}
          pacienteId={pacienteId}
          citaId={citaId}
          clinicaId={clinicaId}
          onSuccess={() => {
            setShowPagoModal(false);
            qc.invalidateQueries({ queryKey: ['pagos-cita', citaId] });
            qc.invalidateQueries({ queryKey: ['consulta-items', citaId] });
          }}
          onClose={() => setShowPagoModal(false)}
        />
      )}
    </div>
  );
}
